import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy | Trademark Organization Name</title>
        <meta name="description" content="Read our privacy policy to understand how we collect, use, and protect your personal information when you use our trademark registration services." />
      </Helmet>

      {/* Header */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center space-x-2">
              <Shield className="w-8 h-8 text-secondary" />
              <span className="text-xl font-bold text-primary">Trademark Organization Name</span>
            </Link>
            <Link to="/">
              <Button className="btn-primary">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-primary mb-8">Privacy Policy</h1>
          
          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                At Trademark Organization Name, we are committed to protecting your privacy and ensuring the security 
                of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard 
                your information when you visit our website and use our trademark registration services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using our services, you acknowledge that you have read, understood, and agree to be 
                bound by this Privacy Policy. If you do not agree with the terms of this privacy policy, please do 
                not access our website or use our services.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Information We Collect</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect information that you voluntarily provide to us when you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Fill out contact forms or inquiry forms on our website</li>
                <li>Request trademark registration services or consultations</li>
                <li>Subscribe to our newsletters or communications</li>
                <li>Interact with our customer support team</li>
                <li>Provide feedback, testimonials, or reviews</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                The personal information we may collect includes:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Full name and contact information (email address, phone number, mailing address)</li>
                <li>Business or company name and related business information</li>
                <li>Trademark information, including names, logos, and descriptions</li>
                <li>Payment and billing information for services rendered</li>
                <li>Communication preferences and correspondence with our team</li>
                <li>Any other information you choose to provide</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Automatically Collected Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you visit our website, we may automatically collect certain information about your device and 
                browsing behavior, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>IP address and location data</li>
                <li>Browser type and version</li>
                <li>Device type and operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website or source</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect for various purposes, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Providing and delivering trademark registration services</li>
                <li>Processing your trademark applications with the USPTO</li>
                <li>Responding to your inquiries and providing customer support</li>
                <li>Sending service-related notifications and updates about your trademark status</li>
                <li>Processing payments and managing billing information</li>
                <li>Improving our website, services, and customer experience</li>
                <li>Sending marketing communications and promotional offers (with your consent)</li>
                <li>Complying with legal obligations and protecting our legal rights</li>
                <li>Detecting and preventing fraud or security issues</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Disclosure of Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Government Agencies:</strong> We share necessary information with the USPTO and other 
                relevant government agencies as required to process your trademark applications.</li>
                <li><strong>Service Providers:</strong> We may share information with trusted third-party service 
                providers who assist us in operating our website, conducting our business, or servicing you, provided 
                they agree to keep this information confidential.</li>
                <li><strong>Legal Requirements:</strong> We may disclose your information when required by law, court 
                order, or government regulation, or when we believe disclosure is necessary to protect our rights, 
                property, or safety.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, 
                your information may be transferred as part of that transaction.</li>
                <li><strong>With Your Consent:</strong> We may share your information for any other purpose with your 
                explicit consent.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
                <li>Encryption of sensitive data during transmission and storage</li>
                <li>Secure server infrastructure and regular security updates</li>
                <li>Access controls and authentication procedures for our staff</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Employee training on data protection and privacy practices</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                However, please note that no method of transmission over the internet or electronic storage is 
                completely secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Access:</strong> Request access to the personal information we hold about you</li>
                <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal 
                obligations</li>
                <li><strong>Objection:</strong> Object to processing of your personal information</li>
                <li><strong>Restriction:</strong> Request restriction of processing your personal information</li>
                <li><strong>Portability:</strong> Request transfer of your information to another service provider</li>
                <li><strong>Withdraw Consent:</strong> Withdraw your consent for processing at any time</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise any of these rights, please contact us using the information provided below. We will 
                respond to your request within a reasonable timeframe as required by applicable law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar tracking technologies to enhance your browsing experience, analyze website 
                traffic, and understand user behavior. Cookies are small text files stored on your device that help 
                us remember your preferences and improve our services.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookie settings through your browser preferences. However, disabling cookies may limit 
                your ability to use certain features of our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Third-Party Links</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website may contain links to third-party websites or services. We are not responsible for the 
                privacy practices or content of these external sites. We encourage you to review the privacy policies 
                of any third-party sites you visit.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal 
                information from children. If you believe we have collected information from a child, please contact 
                us immediately, and we will take steps to delete such information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
                Privacy Policy, unless a longer retention period is required or permitted by law. When we no longer 
                need your information, we will securely delete or anonymize it in accordance with our data retention 
                policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to update or modify this Privacy Policy at any time. When we make changes, we 
                will update the policy on this page and may notify you through email or website notification. Your 
                continued use of our services after any changes constitutes your acceptance of the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-primary mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, 
                please contact us:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700"><strong>Trademark Organization Name</strong></p>
                <p className="text-gray-700">7080 Hollywood Blvd Suite 700</p>
                <p className="text-gray-700">Los Angeles, CA 90028</p>
                <p className="text-gray-700 mt-2">Phone: +1 (123) 123-4567</p>
                <p className="text-gray-700">Email: info@trademarkorganizationname.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
